import React, { memo } from 'react';
import cn from 'classnames';
import { Trans } from 'react-i18next';
import { FormControlLabel, List, ListItem, Radio, RadioGroup } from '@mui/material';

import { useLanguage } from '~/Hooks';
import { EditEventOption } from '~/Screens/Calendar/constants';
import { makeStyles } from '~/UIKit/Style';
import { Link as MuiLink, Typography } from '~/UIKit';
import { ICalendarEventBookings } from '~/Screens/Calendar/interfaces';

export const useStyles = makeStyles(
  () => ({
    list: {
      paddingBottom: 0,
    },
    item: {
      padding: 0,
    },
    itemDisabled: {
      pointerEvents: 'none',
    },
  }),
  { name: 'EditCalendarEventConfirmationModal' },
);

interface IEditCalendarEventModalContent {
  eventName: string;
  selectedOption: EditEventOption;
  setSelectedOption: (selectedOption: EditEventOption) => void;
  confirmationOptions: ICalendarEventBookings;
}

export const EditCalendarEventModalContent = memo(
  ({
    eventName,
    selectedOption,
    setSelectedOption,
    confirmationOptions,
  }: IEditCalendarEventModalContent) => {
    const { classes } = useStyles();
    const { t } = useLanguage();

    if (confirmationOptions.eventHasBookings) {
      return (
        <>
          <Typography variant="body1">
            <Trans
              i18nKey="event.hasBookings.message.part1"
              values={{ eventName }}
              components={{ b: <b /> }}
            />
          </Typography>
          <Typography variant="body1">
            <Trans
              i18nKey="event.hasBookings.message.part2"
              components={[
                <MuiLink
                  key="edit-event-with-bookings"
                  href={t('app.create.classes.link')}
                  rel="noopener noreferrer"
                  target="_blank"
                />,
              ]}
            />
          </Typography>
        </>
      );
    }

    const options = [
      {
        id: EditEventOption.SINGLE_EVENT,
        label: t('event.updateTime.option.singleEvent'),
      },
      {
        id: EditEventOption.EVENT_SERIES,
        label: t('event.updateTime.option.eventSeries'),
        disabled: confirmationOptions.eventSeriesHasBookings,
      },
    ];

    return (
      <>
        <Typography variant="body1">
          <Trans
            i18nKey="event.updateTime.message"
            values={{ eventName }}
            components={{ b: <b /> }}
          />
        </Typography>
        {confirmationOptions.hasMoreThenOneEvent && (
          <List className={classes.list}>
            {options.map(({ id, label, disabled }) => (
              <ListItem
                key={id}
                onClick={() => setSelectedOption(id)}
                className={cn(classes.item, { [classes.itemDisabled]: disabled })}
              >
                <RadioGroup
                  aria-label="edit-event-option"
                  name="edit-event-option"
                  value={id}
                  onChange={() => setSelectedOption(id)}
                >
                  <FormControlLabel
                    disabled={disabled}
                    value={id}
                    control={<Radio />}
                    label={label}
                    checked={selectedOption === id}
                  />
                </RadioGroup>
              </ListItem>
            ))}
          </List>
        )}
      </>
    );
  },
);
